// Postcode flow uses ?addressQuery= (autocomplete). City/region stays in custom-algolia-hooks.
//
// UK postcodes: outward + inward (TW1 | 4LU). ukFull = full code, ukOutward = first part only.
// We geo-search by radius because neighbours usually have a different inward code.
const VENUE_ADDRESS_ATTRIBUTE = "meta.custom.venue_address";
const ADDRESS_QUERY_PARAM = "addressQuery";
const MAP_PADDING = { top: 100, right: 100, bottom: 100, left: 100 };
const SINGLE_VENUE_BOUNDS_OFFSET = 0.012;
const SINGLE_VENUE_MAX_ZOOM = 13;
const SINGLE_VENUE_RETRY_DELAYS = [400, 900, 1600];
const MULTI_VENUE_MAX_ZOOM = 14;
const NO_RESULTS_ZOOM = 12;

const POSTAL_CODE_PATTERNS = [
  /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
  /^[A-Z]{1,2}\d[A-Z\d]?$/i,
  /^\d{5}(-\d{4})?$/,
  /^\d{4}\s?[A-Z]{2}$/i,
  /^\d{4}$/
];

const POSTAL_RADIUS_BY_TYPE = {
  ukFull: 5000, // e.g. TW1 4LU
  ukOutward: 9000, // e.g. TW1
  us: 12000,
  nl: 5000,
  fourDigit: 6000
};

let cachedPostalGeocode = null;

function normalizeUKPostcode(input) {
  const cleaned = String(input || "").replace(/\s+/g, "").toUpperCase();

  // SW129RQ → SW12 9RQ
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(cleaned)) {
    return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
  }

  if (/^[A-Z]{1,2}\d[A-Z\d]?$/.test(cleaned)) {
    return cleaned;
  }

  return String(input || "").trim();
}

function normalizePostcode(input) {
  const trimmed = String(input || "").trim();
  if (!trimmed) return "";

  const compact = trimmed.replace(/\s+/g, "").toUpperCase();
  if (/^[A-Z]{1,2}\d/.test(compact)) {
    return normalizeUKPostcode(trimmed);
  }

  return trimmed.replace(/\s+/g, " ");
}

function isPostalCode(query) {
  const normalized = normalizePostcode(query);
  return Boolean(normalized && POSTAL_CODE_PATTERNS.some((pattern) => pattern.test(normalized)));
}

function getAddressQueryFromUrl(url = window.location.href) {
  const value = new URL(url, window.location.origin).searchParams.get(ADDRESS_QUERY_PARAM);
  return value ? normalizePostcode(value) : null;
}

function buildAddressSearchUrl(query, searchPath, options = {}) {
  const normalizedQuery = normalizePostcode(query);
  const params = new URLSearchParams();

  params.set(ADDRESS_QUERY_PARAM, normalizedQuery);
  params.set("query", normalizedQuery);

  if (options.viewMode) {
    params.set("viewMode", options.viewMode);
  }

  return `${searchPath}?${params.toString()}`;
}

function extractUKOutwardCode(postalCode) {
  const normalized = normalizePostcode(postalCode);
  const match = normalized.match(/^([A-Z]{1,2}\d[A-Z\d]?)/i);
  return match ? match[1].toUpperCase() : null;
}

function getPostalSearchRadius(postalCode) {
  const normalized = normalizePostcode(postalCode);

  if (/^[A-Z]{1,2}\d[A-Z\d]?\s\d[A-Z]{2}$/i.test(normalized)) {
    return POSTAL_RADIUS_BY_TYPE.ukFull;
  }

  if (/^[A-Z]{1,2}\d[A-Z\d]?$/i.test(normalized)) {
    return POSTAL_RADIUS_BY_TYPE.ukOutward;
  }

  if (/^\d{5}(-\d{4})?$/.test(normalized)) {
    return POSTAL_RADIUS_BY_TYPE.us;
  }

  if (/^\d{4}\s?[A-Z]{2}$/i.test(normalized)) {
    return POSTAL_RADIUS_BY_TYPE.nl;
  }

  return POSTAL_RADIUS_BY_TYPE.fourDigit;
}

function getGeoSearchMapInstance() {
  const widget = document.querySelector("algolia-search-widget");
  const language =
    widget?.getAttribute("current-store-language") ||
    window.AlgoliaAppSettingsHelper?.STORE_DEFAULT_LANGUAGE ||
    "en";
  const indexName = window.AlgoliaAppSettingsHelper.getIndexName(language);

  return window.algoliaShopify?.instantsearch?.search?.renderState?.[indexName]?.geoSearch
    ?.widgetParams?.renderState?.mapInstance;
}

function extractGeoPoints(hits) {
  const points = [];

  for (const hit of hits || []) {
    const lat = Number.parseFloat(hit?._geoloc?.lat ?? hit?.meta?.custom?.latitude);
    const lng = Number.parseFloat(hit?._geoloc?.lng ?? hit?.meta?.custom?.longitude);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      points.push({ lat, lng });
    }
  }

  return points;
}

function clampZoom(mapInstance) {
  if (mapInstance?.getZoom?.() > MULTI_VENUE_MAX_ZOOM) {
    mapInstance.setZoom(MULTI_VENUE_MAX_ZOOM);
  }
}

function clampSingleVenueZoom(mapInstance) {
  if (mapInstance?.getZoom?.() > SINGLE_VENUE_MAX_ZOOM) {
    mapInstance.setZoom(SINGLE_VENUE_MAX_ZOOM);
  }
}

function geocodePostalCode(postalCode) {
  return new Promise((resolve) => {
    if (!window.google?.maps?.Geocoder) {
      resolve(null);
      return;
    }

    new window.google.maps.Geocoder().geocode({ address: postalCode }, (results, status) => {
      resolve(status === "OK" ? results[0] : null);
    });
  });
}

function cachePostalGeocode(postalCode, geocoded) {
  if (!geocoded?.geometry?.location) return;

  const lat = geocoded.geometry.location.lat();
  const lng = geocoded.geometry.location.lng();

  cachedPostalGeocode = { postalCode, geocoded, lat, lng };
  window.mapCustomAroundLatLng = `${lat},${lng}`;
}

function getCachedPostalGeocode(postalCode) {
  if (cachedPostalGeocode?.postalCode === postalCode) {
    return cachedPostalGeocode;
  }

  return null;
}

function fitMapGeometry(mapInstance, geometry) {
  if (!geometry) return false;

  if (geometry.viewport) {
    mapInstance.fitBounds(geometry.viewport, MAP_PADDING);
    window.google.maps.event.addListenerOnce(mapInstance, "idle", () => clampZoom(mapInstance));
    return true;
  }

  if (geometry.location) {
    mapInstance.setCenter(geometry.location);
    mapInstance.setZoom(NO_RESULTS_ZOOM);
    return true;
  }

  return false;
}

function setSingleVenueMapView(mapInstance, hits) {
  const point = extractGeoPoints(hits)[0];
  if (!point) return;

  const bounds = new window.google.maps.LatLngBounds();
  bounds.extend({
    lat: point.lat + SINGLE_VENUE_BOUNDS_OFFSET,
    lng: point.lng + SINGLE_VENUE_BOUNDS_OFFSET
  });
  bounds.extend({
    lat: point.lat - SINGLE_VENUE_BOUNDS_OFFSET,
    lng: point.lng - SINGLE_VENUE_BOUNDS_OFFSET
  });

  mapInstance.fitBounds(bounds, MAP_PADDING);

  window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
    clampSingleVenueZoom(mapInstance);
  });
}

function enforceSingleVenueMapView(mapInstance, hits) {
  setSingleVenueMapView(mapInstance, hits);

  // geoSearch forces zoom 20 on a single hit
  const zoomGuard = mapInstance.addListener("zoom_changed", () => {
    clampSingleVenueZoom(mapInstance);
  });

  window.setTimeout(() => {
    window.google.maps.event.removeListener(zoomGuard);
  }, 2500);

  SINGLE_VENUE_RETRY_DELAYS.forEach((delay) => {
    window.setTimeout(() => setSingleVenueMapView(mapInstance, hits), delay);
  });
}

async function applyMapViewport(mapInstance, hits, postalCode) {
  if (!mapInstance || !window.google?.maps) return;

  const points = extractGeoPoints(hits);

  if (points.length === 1) {
    enforceSingleVenueMapView(mapInstance, hits);
    return;
  }

  if (points.length > 1) {
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    mapInstance.fitBounds(bounds, MAP_PADDING);
    window.google.maps.event.addListenerOnce(mapInstance, "idle", () => clampZoom(mapInstance));
    return;
  }

  const cached = getCachedPostalGeocode(postalCode);
  const geocoded = cached?.geocoded || await geocodePostalCode(postalCode);
  fitMapGeometry(mapInstance, geocoded?.geometry);
}

function scheduleMapViewport(hits, postalCode) {
  let attempts = 0;

  const run = async () => {
    const mapInstance = getGeoSearchMapInstance();
    if (mapInstance) {
      await applyMapViewport(mapInstance, hits, postalCode);
      return;
    }

    if (++attempts < 50) {
      window.setTimeout(run, 100);
    }
  };

  run();
}

function getSearchResultCount(helper) {
  return helper?.lastResults?.nbHits ?? 0;
}

async function runGeoPostalSearch(helper, postalCode, geocoded) {
  const lat = geocoded.geometry.location.lat();
  const lng = geocoded.geometry.location.lng();
  const baseRadius = getPostalSearchRadius(postalCode);
  const radii = [baseRadius, Math.round(baseRadius * 1.75)];

  cachePostalGeocode(postalCode, geocoded);

  let searchHelper = helper;

  // sort by distance, ignore venue_address text
  for (const radius of radii) {
    searchHelper = searchHelper
      .setQueryParameter("query", "")
      .setQueryParameter("restrictSearchableAttributes", [])
      .setQueryParameter("aroundLatLng", `${lat},${lng}`)
      .setQueryParameter("aroundRadius", String(radius));

    await searchHelper.search();

    if (getSearchResultCount(searchHelper) > 0) {
      return searchHelper;
    }
  }

  return searchHelper;
}

async function runTextPostalSearch(helper, postalCode) {
  const outwardCode = extractUKOutwardCode(postalCode);
  const textQuery = outwardCode || postalCode;

  return helper
    .setQueryParameter("query", textQuery)
    .setQueryParameter("restrictSearchableAttributes", [VENUE_ADDRESS_ATTRIBUTE])
    .setQueryParameter("aroundLatLng", "")
    .setQueryParameter("aroundRadius", "")
    .search();
}

async function runPostalSearch(helper, postalCode) {
  const geocoded = await geocodePostalCode(postalCode);

  if (geocoded?.geometry?.location) {
    const geoHelper = await runGeoPostalSearch(helper, postalCode, geocoded);

    if (getSearchResultCount(geoHelper) > 0) {
      return geoHelper;
    }
  }

  return runTextPostalSearch(helper, postalCode);
}

function wrapSearchFunction(originalSearchFunction) {
  return async (helper) => {
    const addressQuery = getAddressQueryFromUrl();

    if (!addressQuery) {
      cachedPostalGeocode = null;

      if (typeof originalSearchFunction === "function") {
        return originalSearchFunction(helper);
      }

      return helper.search();
    }

    const widget = document.querySelector("algolia-search-widget");
    if (widget) {
      widget.currentQuery = addressQuery;
    }

    return runPostalSearch(helper, addressQuery);
  };
}

function bindMapListener() {
  const search = window.algoliaShopify?.instantsearch?.search;
  if (!search || search.__postalMapListenerBound) return;

  search.__postalMapListenerBound = true;

  let lastKey = null;

  search.on("render", () => {
    const postalCode = getAddressQueryFromUrl();
    if (!postalCode) {
      lastKey = null;
      return;
    }

    const hits = search.helper?.lastResults?.hits ?? [];
    const key = `${postalCode}|${hits.map((hit) => hit.objectID).join(",")}`;

    if (key === lastKey) return;

    lastKey = key;
    scheduleMapViewport(hits, postalCode);
  });
}

window.AlgoliaPostalSearch = {
  isPostalCode,
  buildAddressSearchUrl
};

document.addEventListener("algolia.hooks.initialize", () => {
  const hooksApi = window.algoliaShopify?.hooks;
  if (!hooksApi || hooksApi.__postalSearchHookRegistered) return;

  hooksApi.__postalSearchHookRegistered = true;

  hooksApi.registerHook("beforeInstantSearchConfigurationOptions", (options) => {
    options.searchFunction = wrapSearchFunction(options.searchFunction);
    return options;
  });

  window.setTimeout(bindMapListener, 0);
});
